<template>
  <div class="space-y-6">
    <!-- GitHub Token 配置提示 -->
    <div class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div class="flex items-start space-x-3">
        <svg class="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
        </svg>
        <div class="flex-1">
          <h3 class="text-sm font-medium text-yellow-800">GitHub Token 未配置</h3>
          <p class="mt-1 text-sm text-yellow-700">
            为了获得更准确的活跃度检查结果，请在 <code class="bg-yellow-100 px-1 rounded">.env</code> 文件中配置 <code class="bg-yellow-100 px-1 rounded">GITHUB_TOKEN</code>。
            没有Token时，GitHub API限制为每小时60次请求，可能导致活跃度检查不准确。
          </p>
          <p class="mt-1 text-xs text-yellow-600">
            获取Token：<a href="https://github.com/settings/tokens" target="_blank" class="underline hover:text-yellow-800">GitHub Settings → Personal Access Tokens</a>
          </p>
        </div>
      </div>
    </div>

    <div class="flex justify-between items-center">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">管理成员</h1>
        <p class="mt-1 text-sm text-gray-600">
          管理 Apache Kafka PMC 成员和自定义追踪成员
        </p>
      </div>
      <div class="flex items-center space-x-3">
        <button
          @click="refreshData"
          :disabled="loading"
          class="btn btn-outline-primary flex items-center space-x-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          <span>刷新</span>
        </button>
        <button
          @click="checkAllActivity"
          :disabled="loading || checkingActivity"
          class="btn btn-primary flex items-center space-x-2"
        >
          <svg v-if="checkingActivity" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>{{ checkingActivity ? '检查中...' : '检查活跃度' }}</span>
        </button>

        <!-- 手动同步按钮 -->
        <ManualSyncButton />

        <!-- 添加成员按钮组 -->
        <div v-if="isEditingAllowed" class="relative">
          <button
            @click="showAddDropdown = !showAddDropdown"
            class="btn btn-primary flex items-center space-x-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            <span>添加成员</span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>

          <!-- 下拉菜单 -->
          <div v-if="showAddDropdown" class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
            <div class="py-1">
              <button
                @click="startAddPMCMember"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                添加PMC成员
              </button>
              <button
                @click="startAddCustomMember"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                添加自定义成员
              </button>
            </div>
          </div>
        </div>

        <!-- 只读模式提示 -->
        <div v-else class="flex items-center space-x-2 text-gray-500">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
          </svg>
          <span class="text-sm">只读模式</span>
        </div>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="grid grid-cols-1 md:grid-cols-6 gap-4">
      <div class="card p-4">
        <div class="text-2xl font-bold text-primary-600">{{ defaultPMCMembers.length }}</div>
        <div class="text-sm text-gray-600">PMC成员</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-green-600">{{ activePMCMembers }}</div>
        <div class="text-sm text-gray-600">活跃PMC</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-red-600">{{ inactivePMCMembers }}</div>
        <div class="text-sm text-gray-600">不活跃PMC</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-blue-600">{{ customMembers.length }}</div>
        <div class="text-sm text-gray-600">自定义成员</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-green-600">{{ activeMembers }}</div>
        <div class="text-sm text-gray-600">激活状态</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-orange-600">{{ totalMembers }}</div>
        <div class="text-sm text-gray-600">总成员数</div>
      </div>
    </div>

    <!-- PMC 成员管理 -->
    <div class="card p-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-medium text-gray-900">Apache Kafka PMC 成员</h2>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-500">{{ defaultPMCMembers.length }} 个PMC成员</span>
          <button
            v-if="isEditingAllowed"
            @click="startAddPMCMember"
            class="btn btn-outline-primary btn-sm"
          >
            添加PMC成员
          </button>
        </div>
      </div>
      
      <!-- 加载状态 -->
      <div v-if="loading && defaultPMCMembers.length === 0" class="text-center py-8">
        <div class="inline-flex items-center space-x-2">
          <svg class="animate-spin h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-gray-600">加载中...</span>
        </div>
      </div>

      <!-- PMC成员列表 -->
      <div v-else-if="defaultPMCMembers.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="member in sortedPMCMembers"
          :key="member.id"
          class="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-primary-50 to-primary-100 hover:shadow-md transition-shadow"
          :class="{ 'opacity-60': !member.active }"
        >
          <div class="flex items-start justify-between">
            <div class="flex items-start space-x-3 flex-1">
              <div class="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              
              <div class="flex-1 min-w-0">
                <div class="flex items-center space-x-2 mb-1">
                  <p class="text-sm font-medium text-gray-900">{{ member.name }}</p>
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    PMC
                  </span>
                  <span
                    :class="{
                      'bg-green-100 text-green-800': isActiveMember(member),
                      'bg-red-100 text-red-800': !isActiveMember(member)
                    }"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ getActivityDescription(member) }}
                  </span>
                  <span
                    v-if="!member.active"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    未激活
                  </span>
                </div>
                
                <div class="text-xs text-gray-600 space-y-1">
                  <div v-if="member.githubId || member.github_id" class="flex items-center space-x-1">
                    <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                    <a
                      :href="`https://github.com/${member.githubId || member.github_id}`"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {{ member.githubId || member.github_id }}
                    </a>
                  </div>
                  <div v-if="member.jiraId || member.jira_id" class="flex items-center space-x-1">
                    <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm1.005-1.005H24a5.218 5.218 0 0 0-5.231-5.215h-2.131V3.236A5.215 5.215 0 0 0 11.425 0v11.482c0 .554.45 1.005 1.005 1.005z"/>
                    </svg>
                    <a
                      :href="`https://issues.apache.org/jira/secure/ViewProfile.jspa?name=${member.jiraId || member.jira_id}`"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {{ member.jiraId || member.jira_id }}
                    </a>
                  </div>
                </div>
                
                <!-- 活跃度信息 -->
                <div v-if="(member.githubActivityScore !== undefined || member.github_activity_score !== undefined) || (member.lastActivityCheck || member.last_activity_check)" class="mt-2 text-xs text-gray-600 space-y-1">
                  <div v-if="member.githubActivityScore !== undefined || member.github_activity_score !== undefined" class="flex items-center space-x-1">
                    <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    <span>活跃度评分: {{ member.githubActivityScore || member.github_activity_score }}</span>
                  </div>
                  <div v-if="member.lastActivityCheck || member.last_activity_check" class="flex items-center space-x-1">
                    <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span>最后检查: {{ formatDate(member.lastActivityCheck || member.last_activity_check) }}</span>
                  </div>
                </div>

                <div v-if="member.notes" class="mt-1 text-xs text-gray-600">
                  {{ member.notes }}
                </div>
              </div>
            </div>
            
            <!-- 操作按钮 -->
            <div v-if="isEditingAllowed" class="flex flex-col space-y-1 ml-2">
              <button
                @click="editPMCMember(member)"
                class="btn btn-outline-primary btn-xs"
              >
                编辑
              </button>
              <button
                @click="togglePMCMemberActivityStatus(member)"
                class="btn btn-xs"
                :class="member.activityStatus === 'active' ? 'btn-outline-orange' : 'btn-outline-green'"
              >
                {{ member.activityStatus === 'active' ? '设为不活跃' : '设为活跃' }}
              </button>
              <button
                @click="togglePMCMemberStatus(member)"
                class="btn btn-xs"
                :class="member.active ? 'btn-outline-orange' : 'btn-outline-green'"
              >
                {{ member.active ? '停用' : '启用' }}
              </button>
              <button
                @click="deletePMCMember(member)"
                class="btn btn-outline-red btn-xs"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">暂无PMC成员</h3>
        <p class="mt-1 text-sm text-gray-500">点击"添加PMC成员"按钮开始添加</p>
      </div>
    </div>

    <!-- 自定义成员列表 -->
    <div class="card p-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-medium text-gray-900">自定义成员</h2>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-500">{{ customMembers.length }} 个自定义成员</span>
          <button
            v-if="isEditingAllowed"
            @click="startAddCustomMember"
            class="btn btn-outline-primary btn-sm"
          >
            添加自定义成员
          </button>
        </div>
      </div>

      <!-- 自定义成员列表 -->
      <div v-if="customMembers.length > 0" class="space-y-4">
        <div
          v-for="member in customMembers"
          :key="member.id"
          class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          :class="{ 'opacity-60': !member.active }"
        >
          <div class="flex items-start justify-between">
            <div class="flex items-start space-x-4">
              <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              
              <div class="flex-1 min-w-0">
                <div class="flex items-center space-x-2 mb-2">
                  <h3 class="text-lg font-medium text-gray-900">{{ member.name }}</h3>
                  <span
                    v-if="!member.active"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    未激活
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    激活
                  </span>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                  <div v-if="member.githubId" class="flex items-center space-x-2">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                    <span>GitHub: </span>
                    <a 
                      :href="`https://github.com/${member.githubId}`" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      class="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {{ member.githubId }}
                    </a>
                  </div>
                  <div v-if="member.jiraId" class="flex items-center space-x-2">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm1.005-1.005H24a5.218 5.218 0 0 0-5.231-5.215h-2.131V3.236A5.215 5.215 0 0 0 11.425 0v11.482c0 .554.45 1.005 1.005 1.005z"/>
                    </svg>
                    <span>JIRA: </span>
                    <a 
                      :href="`https://issues.apache.org/jira/secure/ViewProfile.jspa?name=${member.jiraId}`" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      class="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {{ member.jiraId }}
                    </a>
                  </div>
                  <div v-if="member.email" class="flex items-center space-x-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                    <span>{{ member.email }}</span>
                  </div>
                  <div class="flex items-center space-x-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span>{{ formatDate(member.createdAt) }}</span>
                  </div>
                </div>
                
                <div v-if="member.notes" class="mt-2 text-sm text-gray-600">
                  <span class="font-medium">备注:</span> {{ member.notes }}
                </div>
              </div>
            </div>
            
            <div v-if="isEditingAllowed" class="flex items-center space-x-2 ml-4">
              <button
                @click="editCustomMember(member)"
                class="btn btn-outline-primary btn-sm"
              >
                编辑
              </button>
              <button
                @click="toggleCustomMemberStatus(member)"
                class="btn btn-sm"
                :class="member.active ? 'btn-outline-orange' : 'btn-outline-green'"
              >
                {{ member.active ? '停用' : '启用' }}
              </button>
              <button
                @click="deleteCustomMember(member)"
                class="btn btn-outline-red btn-sm"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">暂无自定义成员</h3>
        <p class="mt-1 text-sm text-gray-500">点击"添加自定义成员"按钮开始添加</p>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <svg class="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
        </svg>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">加载失败</h3>
          <p class="mt-1 text-sm text-red-700">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- 添加/编辑成员模态框 -->
    <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-medium text-gray-900">
              {{ modalTitle }}
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <form @submit.prevent="saveMember" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">姓名 *</label>
              <input
                v-model="memberForm.name"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="输入成员姓名"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">GitHub ID</label>
              <input
                v-model="memberForm.githubId"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="GitHub 用户名"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">JIRA ID</label>
              <input
                v-model="memberForm.jiraId"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="JIRA 用户名"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">邮箱</label>
              <input
                v-model="memberForm.email"
                type="email"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="邮箱地址"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">备注</label>
              <textarea
                v-model="memberForm.notes"
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="添加备注信息"
              ></textarea>
            </div>
            
            <div v-if="editingMember" class="flex items-center">
              <input
                v-model="memberForm.active"
                type="checkbox"
                id="active"
                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label for="active" class="ml-2 block text-sm text-gray-900">激活状态</label>
            </div>
            
            <div class="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                @click="closeModal"
                class="btn btn-outline-gray"
              >
                取消
              </button>
              <button
                type="submit"
                :disabled="!memberForm.name || (!memberForm.githubId && !memberForm.jiraId)"
                class="btn btn-primary"
              >
                {{ editingMember ? '更新' : '添加' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores'
import ManualSyncButton from '@/components/ManualSyncButton.vue'
import { checkEditingPermission } from '@/config/app.js'

const store = useAppStore()

// 响应式状态
const { loading, error, customMembers, defaultPMCMembers } = storeToRefs(store)

// 本地状态
const showModal = ref(false)
const showAddDropdown = ref(false)
const editingMember = ref(null)
const isEditingPMC = ref(false)
const checkingActivity = ref(false)
const memberForm = ref({
  name: '',
  githubId: '',
  jiraId: '',
  email: '',
  notes: '',
  active: true
})

// 计算属性
const activeMembers = computed(() => {
  const activePMC = defaultPMCMembers.value.filter(member => member.active).length
  const activeCustom = customMembers.value.filter(member => member.active).length
  return activePMC + activeCustom
})

const activePMCMembers = computed(() => {
  return defaultPMCMembers.value.filter(member => {
    // 优先使用数据库中的 activity_status
    if (member.activity_status) {
      return member.active && member.activity_status === 'active'
    }
    // 回退到计算的活跃度
    return member.active && isActiveMember(member)
  }).length
})

const inactivePMCMembers = computed(() => {
  return defaultPMCMembers.value.filter(member => {
    // 优先使用数据库中的 activity_status
    if (member.activity_status) {
      return member.active && member.activity_status === 'inactive'
    }
    // 回退到计算的活跃度
    return member.active && !isActiveMember(member)
  }).length
})

// 按活跃度排序的 PMC 成员
const sortedPMCMembers = computed(() => {
  return [...defaultPMCMembers.value]
    .filter(member => member.active)
    .sort((a, b) => {
      // 首先按活跃状态排序：活跃成员优先
      const isActiveA = isActiveMember(a)
      const isActiveB = isActiveMember(b)

      if (isActiveA !== isActiveB) {
        return isActiveB - isActiveA // 活跃成员排在前面
      }

      // 相同活跃状态下，按活跃度分数排序
      const scoreA = calculateActivityScore(a)
      const scoreB = calculateActivityScore(b)

      if (scoreA !== scoreB) {
        return scoreB - scoreA // 分数高的排在前面
      }

      return a.name.localeCompare(b.name) // 相同分数按名字排序
    })
})

const totalMembers = computed(() => {
  return defaultPMCMembers.value.length + customMembers.value.length
})

// 编辑权限控制
const isEditingAllowed = computed(() => {
  return checkEditingPermission()
})

const modalTitle = computed(() => {
  if (editingMember.value) {
    return isEditingPMC.value ? '编辑PMC成员' : '编辑自定义成员'
  } else {
    return isEditingPMC.value ? '添加PMC成员' : '添加自定义成员'
  }
})

// 方法
const formatDate = (dateString) => {
  if (!dateString) return '未知'
  
  const date = new Date(dateString)
  const now = new Date()
  const diffInHours = (now - date) / (1000 * 60 * 60)
  
  if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}小时前`
  } else if (diffInHours < 24 * 7) {
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays}天前`
  } else {
    return date.toLocaleDateString('zh-CN')
  }
}

const refreshData = async () => {
  try {
    await store.fetchAllMembers()
  } catch (error) {
    console.error('Failed to refresh members data:', error)
  }
}

// 检查所有成员活跃度
const checkAllActivity = async () => {
  try {
    checkingActivity.value = true

    console.log('🚀 开始检查所有PMC成员活跃度...')

    // 调用活跃度检查API
    const response = await fetch('/api/activity/check-all', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ force: true }) // 强制检查所有成员
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const result = await response.json()

    if (result.success) {
      console.log(`✅ 活跃度检查完成！检查了 ${result.checked} 个成员，更新了 ${result.updated} 个成员`)

      // 刷新数据以显示最新的活跃度状态
      await refreshData()

      // 显示成功消息
      alert(`活跃度检查完成！\n检查了 ${result.checked} 个成员\n更新了 ${result.updated} 个成员`)
    } else {
      throw new Error(result.error || '活跃度检查失败')
    }

  } catch (error) {
    console.error('活跃度检查失败:', error)
    alert(`活跃度检查失败: ${error.message}`)
  } finally {
    checkingActivity.value = false
  }
}

// PMC成员相关方法
const startAddPMCMember = () => {
  isEditingPMC.value = true
  editingMember.value = null
  memberForm.value = {
    name: '',
    githubId: '',
    jiraId: '',
    email: '',
    notes: '',
    active: true
  }
  showModal.value = true
  showAddDropdown.value = false
}

const editPMCMember = (member) => {
  isEditingPMC.value = true
  editingMember.value = member
  memberForm.value = {
    name: member.name,
    githubId: member.githubId || member.github_id || '',
    jiraId: member.jiraId || member.jira_id || '',
    email: member.email || '',
    notes: member.notes || '',
    active: member.active
  }
  showModal.value = true
}

const togglePMCMemberStatus = async (member) => {
  try {
    await store.updateDefaultPMCMember(member.id, {
      ...member,
      active: !member.active
    })
  } catch (error) {
    console.error('Failed to toggle PMC member status:', error)
  }
}

const togglePMCMemberActivityStatus = async (member) => {
  try {
    const newActivityStatus = member.activityStatus === 'active' ? 'inactive' : 'active'
    await store.updateDefaultPMCMember(member.id, {
      ...member,
      activityStatus: newActivityStatus
    })
  } catch (error) {
    console.error('Failed to toggle PMC member activity status:', error)
  }
}

const deletePMCMember = async (member) => {
  if (confirm(`确定要删除PMC成员 "${member.name}" 吗？此操作无法撤销。`)) {
    try {
      await store.deleteDefaultPMCMember(member.id)
    } catch (error) {
      console.error('Failed to delete PMC member:', error)
    }
  }
}

// 自定义成员相关方法
const startAddCustomMember = () => {
  isEditingPMC.value = false
  editingMember.value = null
  memberForm.value = {
    name: '',
    githubId: '',
    jiraId: '',
    email: '',
    notes: '',
    active: true
  }
  showModal.value = true
  showAddDropdown.value = false
}

const editCustomMember = (member) => {
  isEditingPMC.value = false
  editingMember.value = member
  memberForm.value = {
    name: member.name,
    githubId: member.githubId || member.github_id || '',
    jiraId: member.jiraId || member.jira_id || '',
    email: member.email || '',
    notes: member.notes || '',
    active: member.active
  }
  showModal.value = true
}

const toggleCustomMemberStatus = async (member) => {
  try {
    await store.updateCustomMember(member.id, {
      ...member,
      active: !member.active
    })
  } catch (error) {
    console.error('Failed to toggle custom member status:', error)
  }
}

const deleteCustomMember = async (member) => {
  if (confirm(`确定要删除自定义成员 "${member.name}" 吗？此操作无法撤销。`)) {
    try {
      await store.deleteCustomMember(member.id)
    } catch (error) {
      console.error('Failed to delete custom member:', error)
    }
  }
}

// 活跃度计算函数 - 基于数据库中的真实活动数据
const calculateActivityScore = (member) => {
  // 使用数据库中存储的 GitHub 活跃度评分
  if (member.github_activity_score !== undefined && member.github_activity_score !== null) {
    return member.github_activity_score
  } else if (member.githubActivityScore !== undefined && member.githubActivityScore !== null) {
    return member.githubActivityScore
  }

  // 如果没有活跃度数据，默认为 0（不活跃）
  return 0
}

// 判断成员是否活跃（基于数据库状态）
const isActiveMember = (member) => {
  // 优先使用数据库中存储的活跃状态
  if (member.activity_status) {
    return member.activity_status === 'active'
  }

  // 如果没有活跃度状态，但有检查记录，基于评分判断
  if (member.last_activity_check || member.lastActivityCheck) {
    const score = calculateActivityScore(member)
    return score > 0  // 只要有任何活动就认为活跃
  }

  // 如果没有任何检查记录，默认认为需要检查（显示为不活跃，提示用户检查）
  return false
}

// 获取活跃度描述
const getActivityDescription = (member) => {
  return isActiveMember(member) ? '活跃' : '不活跃'
}

// 通用方法
const saveMember = async () => {
  try {
    if (editingMember.value) {
      // 更新成员
      if (isEditingPMC.value) {
        await store.updateDefaultPMCMember(editingMember.value.id, memberForm.value)
      } else {
        await store.updateCustomMember(editingMember.value.id, memberForm.value)
      }
    } else {
      // 添加新成员
      if (isEditingPMC.value) {
        await store.addDefaultPMCMember(memberForm.value)
      } else {
        await store.addCustomMember(memberForm.value)
      }
    }
    closeModal()
  } catch (error) {
    console.error('Failed to save member:', error)
  }
}

const closeModal = () => {
  showModal.value = false
  editingMember.value = null
  isEditingPMC.value = false
  memberForm.value = {
    name: '',
    githubId: '',
    jiraId: '',
    email: '',
    notes: '',
    active: true
  }
}



// 点击外部关闭下拉菜单
const handleClickOutside = (event) => {
  if (!event.target.closest('.relative')) {
    showAddDropdown.value = false
  }
}

// 生命周期
onMounted(() => {
  refreshData()
  document.addEventListener('click', handleClickOutside)
})

// 清理事件监听器
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script> 